/**
 * Trainer Validation Schemas
 *
 * Zod schemas for the trainer's write payloads — sending a bump, and declaring
 * or ending a training hold. Client-side validation mirroring
 * `StoreTrainerBumpRequest` and `StoreTrainingHoldRequest`, so a coach learns
 * about a bad date before the round trip rather than from a 422.
 *
 * The server stays authoritative. These exist to fail fast and, for the hold
 * flow, to drive the form — the same schema backs the resolver and the service
 * guard, so the rules cannot drift between the two.
 *
 * Features:
 * - Bump payloads: exactly one of `reason` / `alert_id`
 * - Hold payloads: reason enum, bounded dates, cross-field ordering
 * - Input/Output type exports
 * - Validation helper functions (validate, safeValidate, isValid)
 */

import { planScheduleMapSchema } from './plan-schedule.schemas';
import {
  deloadEveryWeeksSchema,
  templateWorkoutSchema,
} from './template.schemas';
import { z } from './zod';
import {
  TRAINER_BILLING_CYCLES,
  TRAINER_BUMP_REASONS,
  TRAINING_HOLD_REASONS,
} from '../types/domain/trainer.types';
import {
  CURRENCIES,
  CURRENCY_MINOR_DIGITS,
} from '../types/user-preferences.types';
import { dayjs } from '../utils/date';

// =============================================================================
// COMMON VALIDATION RULES
// =============================================================================

/**
 * A `Y-m-d` calendar date, matching `dateFormat()` server-side.
 *
 * Deliberately a string rather than `z.date()`: these fields are calendar days
 * the trainer picks, not instants, and round-tripping them through `Date`
 * introduces a timezone the payload does not have.
 */
const calendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a YYYY-MM-DD date')
  .refine((value) => dayjs(value, 'YYYY-MM-DD', true).isValid(), {
    message: 'That is not a real date',
  });

/** Why a hold was declared. The server derives the hold's *kind* from this. */
export const trainingHoldReasonSchema = z.enum(
  TRAINING_HOLD_REASONS as unknown as [string, ...string[]]
);

/** Why a bump was sent. Note `acwr_risk` is absent — an injury-risk alert has no bump. */
export const trainerBumpReasonSchema = z.enum(
  TRAINER_BUMP_REASONS as unknown as [string, ...string[]]
);

// =============================================================================
// BUMP
// =============================================================================

/**
 * A bump is scoped by an alert or by a reason, never both.
 *
 * Mirrors `required_without` + `prohibits` server-side. The alert path is
 * preferred where one exists, because the reason is then inherited from the
 * alert rather than guessed.
 *
 * This schema does **not** encode whether the bump is *allowed* — that is the
 * alert's `bumpable` flag, which folds in the ACWR carve-out, rest holds and an
 * already-answered alert. A valid payload can still be a refused one.
 */
export const sendBumpSchema = z
  .object({
    reason: trainerBumpReasonSchema.optional(),
    alert_id: z.string().trim().min(1, 'An alert id is required').optional(),
  })
  .superRefine((value, ctx) => {
    const hasReason = value.reason !== undefined;
    const hasAlert = value.alert_id !== undefined;

    if (hasReason && hasAlert) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Send a bump for an alert or for a reason, not both',
        path: ['alert_id'],
      });
    }

    if (!hasReason && !hasAlert) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A bump needs either an alert or a reason',
        path: ['reason'],
      });
    }
  });

export type SendBumpSchemaInput = z.input<typeof sendBumpSchema>;
export type SendBumpSchemaOutput = z.output<typeof sendBumpSchema>;

// =============================================================================
// TRAINING HOLD
// =============================================================================

/**
 * Declaring a hold.
 *
 * Note there is no `kind`. The server derives it from the reason
 * (`TrainingHoldReason::kind()`) — rest for post-op, injury, illness, travel,
 * personal and **deload**; load for peaking blocks and intentional overreach.
 * Accepting a client-supplied kind would let `{ kind: 'load', reason: 'post_op' }`
 * through, which is a contradiction the type system cannot catch.
 */
export const declareTrainingHoldSchema = z
  .object({
    reason: trainingHoldReasonSchema,

    /**
     * Backdating is capped at the start of the current week, mirroring the
     * server. A hold reaching further back could retire a bad month after the
     * fact, which would make every score it touches unfalsifiable.
     */
    starts_on: calendarDateSchema.refine(
      (value) =>
        dayjs(value, 'YYYY-MM-DD', true).isSameOrAfter(
          dayjs().startOf('week'),
          'day'
        ),
      { message: 'A hold cannot start before this week' }
    ),

    /** Omitted or null means open-ended: it never auto-returns. */
    expected_end: calendarDateSchema.nullable().optional(),

    /** Coaching context, rendered verbatim. Not for clinical detail. */
    note: z
      .string()
      .trim()
      .max(255, 'Keep the note under 255 characters')
      .nullable()
      .optional(),
  })
  .refine(
    (value) =>
      !value.expected_end ||
      dayjs(value.expected_end, 'YYYY-MM-DD', true).isSameOrAfter(
        dayjs(value.starts_on, 'YYYY-MM-DD', true),
        'day'
      ),
    {
      message: 'A hold cannot end before it starts',
      path: ['expected_end'],
    }
  );

export type DeclareTrainingHoldSchemaInput = z.input<
  typeof declareTrainingHoldSchema
>;
export type DeclareTrainingHoldSchemaOutput = z.output<
  typeof declareTrainingHoldSchema
>;

// =============================================================================
// CHANGE PROPOSALS
// =============================================================================

/**
 * A hashid, as every proposal payload names its subject by one.
 *
 * Never a number and never sorted on — `HashidExists` decodes it server-side,
 * so a numeric primary key sent here resolves to the wrong row or to none.
 */
const hashidSchema = z.string().trim().min(1, 'An id is required');

/**
 * Why the trainer is asking. **Required on every proposal**, unlike a hold's
 * optional note.
 *
 * A diff is a receipt; the note is the coaching, and it is the only part that
 * says *why*. A client answering a list of changed numbers with no reason
 * attached is being asked to consent to something nobody explained.
 *
 * 530 rather than the hold note's 255, matching all three store requests.
 */
export const proposalNoteSchema = z
  .string()
  .trim()
  .min(1, 'Say why you are proposing this')
  .max(530, 'Keep the note under 530 characters');

/**
 * Changing the sets in a client's plan.
 *
 * Composes {@link templateWorkoutSchema} rather than restating the workout
 * shape — this payload *is* a plan template, and a second definition of it is a
 * second thing to keep in step with the server.
 *
 * No `kind`: the server derives it from the route. At least one workout,
 * because removing a session from a client's week is a schedule change and
 * emptying their plan is not a proposal.
 */
export const proposePlanChangeSchema = z.object({
  plan_id: hashidSchema,
  note: proposalNoteSchema,
  workouts: z
    .array(templateWorkoutSchema)
    .min(1, 'A proposal needs at least one workout'),
  /**
   * Send only when the trainer changed it: an omitted key leaves the client's
   * cadence as it is, while `null` clears it.
   */
  deload_every_weeks: deloadEveryWeeksSchema,
});

/**
 * Changing which days a client's plan asks for.
 *
 * The schedule is a date-keyed map, so a date is occupied rather than
 * appendable — one session per date is the shape, not a rule layered over it.
 */
export const proposeScheduleChangeSchema = z.object({
  plan_id: hashidSchema,
  note: proposalNoteSchema,
  schedule: planScheduleMapSchema.refine(
    (value) => Object.keys(value).length > 0,
    { message: 'A proposal needs at least one session' }
  ),
});

/**
 * Changing a client's goal.
 *
 * **Only the target and the date.** Type and direction are excluded because
 * changing either replaces the goal rather than adjusting it, and
 * cancel-and-recreate is the honest path; name and description because they do
 * not affect velocity; starting value because it rewrites what progress has
 * already been measured against, which is a correction to the client's own
 * record and theirs to make.
 *
 * What remains are exactly the two forward-looking parameters, which is why
 * every goal proposal summarises as a change in required pace.
 */
export const proposeGoalChangeSchema = z
  .object({
    goal_id: hashidSchema,
    note: proposalNoteSchema,
    target_value: z
      .number()
      .min(0, 'A target cannot be negative')
      .nullable()
      .optional(),
    // Forward-looking by definition: a target date in the past is not a
    // deadline, and the goal would read as overdue the moment it applied.
    target_date: calendarDateSchema
      .refine(
        (value) => dayjs(value, 'YYYY-MM-DD', true).isAfter(dayjs(), 'day'),
        {
          message: 'A target date has to be in the future',
        }
      )
      .nullable()
      .optional(),
  })
  .superRefine((value, ctx) => {
    const hasValue =
      value.target_value !== undefined && value.target_value !== null;
    const hasDate =
      value.target_date !== undefined && value.target_date !== null;

    // Mirrors `required_without` on both, so an empty proposal is refused here
    // rather than persisted as a no-op the client is then asked to agree to.
    if (!hasValue && !hasDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Change the target, the date, or both',
        path: ['target_value'],
      });
    }
  });

/**
 * The terms a trainer sets when accepting a client request, mirroring
 * `StoreTrainerClientContractRequest`. The API takes a fee with up to two
 * decimals (`decimal:0,2`); parsing still normalises it to exactly two, so
 * every contract is recorded the same way — a trainer types `120`, the
 * payload says `"120.00"`.
 *
 * The end date must be after today, as the server's `after:today` requires —
 * checked here so the trainer hears it before the round trip.
 *
 * A fee in a currency with no minor unit (the yen) must be whole. The API
 * would take `1000.50` and store it, but shows it back rounded to `¥1,001`,
 * so the fraction is refused here rather than kept where no one can see it.
 */
export const acceptClientTermsSchema = z
  .object({
    currency: z.enum(CURRENCIES as unknown as [string, ...string[]], {
      error: 'Choose a currency',
    }),

    /** Up to 9,999,999.99 — `trainer_client.fee` is an unsigned int of cents. */
    fee: z
      .string()
      .trim()
      .min(1, 'Enter the fee, or 0 if it’s free')
      .regex(/^\d{1,7}(\.\d{1,2})?$/, 'Enter an amount like 120 or 120.50')
      .transform((value) => Number(value).toFixed(2)),

    cycle: z.enum(TRAINER_BILLING_CYCLES as unknown as [string, ...string[]], {
      error: 'Choose how often they’re billed',
    }),

    ends_at: z
      .string()
      .min(1, 'Choose when the contract ends')
      .pipe(calendarDateSchema)
      .refine(
        (value) => dayjs(value, 'YYYY-MM-DD', true).isAfter(dayjs(), 'day'),
        { message: 'Choose a date after today' }
      ),
  })
  .superRefine((value, ctx) => {
    const minorDigits =
      CURRENCY_MINOR_DIGITS[
        value.currency as keyof typeof CURRENCY_MINOR_DIGITS
      ];

    // Only a fee that parsed: a malformed one already has its own message.
    const parsed = /^\d+\.\d{2}$/.test(value.fee);

    if (minorDigits === 0 && parsed && !value.fee.endsWith('.00')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${value.currency} has no decimals — enter a whole amount, like 1200`,
        path: ['fee'],
      });
    }
  });

export type AcceptClientTermsSchemaInput = z.input<
  typeof acceptClientTermsSchema
>;
export type AcceptClientTermsSchemaOutput = z.output<
  typeof acceptClientTermsSchema
>;

export type ProposePlanChangeSchemaInput = z.input<
  typeof proposePlanChangeSchema
>;
export type ProposePlanChangeSchemaOutput = z.output<
  typeof proposePlanChangeSchema
>;
export type ProposeScheduleChangeSchemaInput = z.input<
  typeof proposeScheduleChangeSchema
>;
export type ProposeScheduleChangeSchemaOutput = z.output<
  typeof proposeScheduleChangeSchema
>;
export type ProposeGoalChangeSchemaInput = z.input<
  typeof proposeGoalChangeSchema
>;
export type ProposeGoalChangeSchemaOutput = z.output<
  typeof proposeGoalChangeSchema
>;

// =============================================================================
// VALIDATION HELPER FUNCTIONS
// =============================================================================

/**
 * Validate a bump payload
 * @param data - The data to validate
 * @returns The validated and typed bump payload
 * @throws ZodError if validation fails
 */
export function validateSendBump(data: unknown): SendBumpSchemaOutput {
  return sendBumpSchema.parse(data);
}

/**
 * Safely validate a bump payload
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateSendBump(data: unknown) {
  return sendBumpSchema.safeParse(data);
}

/**
 * Check if data is a valid bump payload
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidSendBump(data: unknown): boolean {
  return sendBumpSchema.safeParse(data).success;
}

/**
 * Validate a declare-hold payload
 * @param data - The data to validate
 * @returns The validated and typed hold payload
 * @throws ZodError if validation fails
 */
export function validateDeclareTrainingHold(
  data: unknown
): DeclareTrainingHoldSchemaOutput {
  return declareTrainingHoldSchema.parse(data);
}

/**
 * Safely validate a declare-hold payload
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateDeclareTrainingHold(data: unknown) {
  return declareTrainingHoldSchema.safeParse(data);
}

/**
 * Check if data is a valid declare-hold payload
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidDeclareTrainingHold(data: unknown): boolean {
  return declareTrainingHoldSchema.safeParse(data).success;
}

// -----------------------------------------------------------------------------
// Change proposals
// -----------------------------------------------------------------------------

/**
 * Validate a plan-workouts proposal payload
 * @param data - The data to validate
 * @returns The validated and typed payload
 * @throws ZodError if validation fails
 */
export function safeValidateAcceptClientTerms(
  data: unknown
): z.ZodSafeParseResult<AcceptClientTermsSchemaOutput> {
  return acceptClientTermsSchema.safeParse(data);
}

export function validateProposePlanChange(
  data: unknown
): ProposePlanChangeSchemaOutput {
  return proposePlanChangeSchema.parse(data);
}

/**
 * Safely validate a plan-workouts proposal payload
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateProposePlanChange(data: unknown) {
  return proposePlanChangeSchema.safeParse(data);
}

/**
 * Validate a schedule proposal payload
 * @param data - The data to validate
 * @returns The validated and typed payload
 * @throws ZodError if validation fails
 */
export function validateProposeScheduleChange(
  data: unknown
): ProposeScheduleChangeSchemaOutput {
  return proposeScheduleChangeSchema.parse(data);
}

/**
 * Safely validate a schedule proposal payload
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateProposeScheduleChange(data: unknown) {
  return proposeScheduleChangeSchema.safeParse(data);
}

/**
 * Validate a goal proposal payload
 * @param data - The data to validate
 * @returns The validated and typed payload
 * @throws ZodError if validation fails
 */
export function validateProposeGoalChange(
  data: unknown
): ProposeGoalChangeSchemaOutput {
  return proposeGoalChangeSchema.parse(data);
}

/**
 * Safely validate a goal proposal payload
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateProposeGoalChange(data: unknown) {
  return proposeGoalChangeSchema.safeParse(data);
}

/**
 * Safely validate a proposal note on its own.
 *
 * The note is collected separately from the rest of a plan or schedule
 * proposal — it is asked for after the edit, in its own dialog — so it needs a
 * validator that does not require the payload around it. Same rule either way:
 * the full create schemas compose this exact field.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateProposalNote(data: unknown) {
  return proposalNoteSchema.safeParse(data);
}
